import { isEmptyString, safeGetTextAsJson, digProperty,safeGetResponseBodyAsText, isEmptyArray } from "./utility";
const ComponentName = "common/API";

export const serviceWakeUp = async (type /* full | managed */) => {
	const FunctionName = "serviceWakeUp";
	try {
		let endpoint = "/api/swaWakeup";
		if (!isEmptyString(type)) {
			endpoint = `${endpoint}?type=${type}`;
		}
		const response = await fetch(endpoint, {
			method: "GET",
			headers: {
				"Accept": "application/json",
			}
		});

		const responseStatus = response?.status ?? 0;
		const responseStatusText = response?.statusText || "";
		const responseText = await safeGetResponseBodyAsText(response);
		let responseData = null;
		if (response.ok) {
			// 200...299 response
			responseData = safeGetTextAsJson(responseText) || null;
			console.debug(ComponentName, `${FunctionName}() responseData`, responseData);
		} else {
			// 300...5xx response
			console.debug(ComponentName, `${FunctionName}() responseText [status=${responseStatus}]`, responseText);
			if (!isEmptyString(responseText)) {
				const errorResponseData = safeGetTextAsJson(responseText);
				throw errorResponseData || new Error(`The MGUMS service returned error ${responseStatus} ${responseStatusText}: ${responseText}`);
			} else {
				throw new Error(`The MGUMS service returned error: ${responseStatus} ${responseStatusText}`);
			}
		}

		return responseData || null;
	} catch (error) {
		const errMessage = digProperty(error, "message") || "";
		error.message = `${ComponentName}::${FunctionName}() ➜ Error calling service wakeup: ${errMessage}`;
		console.error(`Error calling service wakeup: ${error?.message || "Unknown"}`, error);
		if (error instanceof Error) {
			error.name = `${ComponentName}::${FunctionName}()`;
		}
		throw error;
	}
};

/**
 * Reads organisations associated with the supplied email domain.
 * @param {string} emailDomain - The email address or email domain to match with organisations
 * @returns {Promise} Resolves with an array containing matching organisations
 * ```
 * [
 *		{
 *			memberId: "253970",
 *			memberName: "019Mobile",
 *			memberStatus: "Active" | "Discontinued" | "Suspended"
 *		},
 * ]
 * ```
 */
export const readOrganisationsByEmailDomain = async (emailDomain) => {
	const FunctionName = "readOrganisationsByEmailDomain";
	try {
		let endpoint = "/api/swaReadOrganisations";
		if (!isEmptyString(emailDomain)) {
			/* remove leading/trailing space, remove leading text before '@' if present */
			const domain = `${emailDomain || ""}`.trim().replace(/^[^@]+@/gi, "");
			endpoint = `${endpoint}?emaildomain=${domain.trim().toLowerCase()}`;
		}
		const response = await fetch(endpoint, {
			method: "GET",
			headers: {
				"Accept": "application/json",
			}
		});

		const responseStatus = response?.status ?? 0;
		const responseStatusText = response?.statusText || "";
		const responseText = await safeGetResponseBodyAsText(response);
		let responseData = null;
		if (response.ok) {
			// 200...299 response
			responseData = safeGetTextAsJson(responseText) || null;
			console.debug(ComponentName, `${FunctionName}() responseData`, { responseData });
		} else {
			// 300...5xx response
			console.debug(ComponentName, `${FunctionName}() responseText [status=${responseStatus}]`, { responseText });
			if (!isEmptyString(responseText)) {
				const errorResponseData = safeGetTextAsJson(responseText);
				throw errorResponseData || new Error(`The MGUMS service returned error ${responseStatus} ${responseStatusText}: ${responseText}`);
			} else {
				throw new Error(`The MGUMS service returned error: ${responseStatus} ${responseStatusText}`);
			}
		}

		return responseData?.value || [];
	} catch (error) {
		const errMessage = digProperty(error, "message") || "";
		error.message = `${ComponentName}::${FunctionName}() ➜ Error fetching organisations: ${errMessage}`;
		console.error(`Error fetching organisations: ${error?.message || "Unknown"}`, error);
		if (error instanceof Error) {
			error.name = `${ComponentName}::${FunctionName}()`;
		}
		throw error;
	}
};

/** * Prevalidates an email address against the MGUMS service to check if it is associated with any organisation.
 * @param {string} emailAddress - The email address to prevalidate.
 * @returns {Promise} Resolves with a object value
 * ```
{
   // True = email address is NOT associated with a directory account, False = email address IS associated with a directory account
   value: boolean;
   // if value = False, provides the directory member account and organisation details
	directoryMember: {
		id: string;
		displayName: string;
		firstName: string;
		lastName: string;
		email: string;
		memberOrganisations: string[];
	}
}
 * ```
 * @throws {Error} If the email address is not provided or if there is an error fetching the data.
 */
export const prevalidateAccountByEmail = async (emailAddress) => {
	const FunctionName = "prevalidateAccountByEmail";
	try {
		const endpoint = `/api/swaPrevalidateAccountByEmail?emailaddress=${(emailAddress || "").trim().toLowerCase()}`;
		const response = await fetch(endpoint, {
			method: "GET",
			headers: {
				"Accept": "application/json",
			}
		});

		const responseStatus = response?.status ?? 0;
		const responseStatusText = response?.statusText || "";
		const responseText = await safeGetResponseBodyAsText(response);
		let responseData = null;
		if (response.ok) {
			// 200...299 response
			responseData = safeGetTextAsJson(responseText) || null;
			console.debug(ComponentName, `${FunctionName}() responseData`, { responseData });
		} else {
			// 300...5xx response
			console.debug(ComponentName, `${FunctionName}() responseText [status=${responseStatus}]`, { responseText });
			if (!isEmptyString(responseText)) {
				const errorResponseData = safeGetTextAsJson(responseText);
				throw errorResponseData || new Error(`The MGUMS service returned error ${responseStatus} ${responseStatusText}: ${responseText}`);
			} else {
				throw new Error(`The MGUMS service returned error: ${responseStatus} ${responseStatusText}`);
			}
		}

		return responseData || null;
	} catch (error) {
		const errMessage = digProperty(error, "message") || "";
		error.message = `${ComponentName}::${FunctionName}() ➜ Error prevalidating email address: ${errMessage}`;
		console.error(`Error fetching organisations: ${error?.message || "Unknown"}`, error);
		if (error instanceof Error) {
			error.name = `${ComponentName}::${FunctionName}()`;
		}
		throw error;
	}
};


/**
 * Validates a user access request by checking if the user is already registered with the organisation.
 * @param {string} emailAddress - The email address of the user requesting access.
 * @param {string} organisationMemberId - The ID of the organisation member.
 * @returns {Promise} Resolves with a boolean indicating if the user access request is valid.
 * ```
 * true = user access request is valid (no pending request for the email address and organisation member id)
 * false = user access request is not valid (existing pending request for the email address and organisation member id)
 * ```
 * @throws {Error} If the email address or organisation member ID is not provided, or if there is an error fetching the data.
 * @example
 * const isValid = await validateUserAccessRequest("bob@gmail.com", "253970");
 * // isValid will be true or false based on the user's registration status
 */
export const validateUserAccessRequest = async (emailAddress, organisationMemberId) => {
	const FunctionName = "validateUserAccessRequest";
	try {
		if (isEmptyString(emailAddress)) {
			throw new Error("Email address is required to validate user access request.");
		}
		if (isEmptyString(organisationMemberId)) {
			throw new Error("Organisation member ID is required to validate user access request.");
		}
		const endpoint = `/api/swaValidateUserAccessRequest?emailaddress=${emailAddress.trim().toLowerCase()}&memberid=${organisationMemberId.trim()}`;
		const response = await fetch(endpoint, {
			method: "GET",
			headers: {
				"Accept": "application/json",
			}
		});

		const responseStatus = response?.status ?? 0;
		const responseStatusText = response?.statusText || "";
		const responseText = await safeGetResponseBodyAsText(response);
		let responseData = null;
		if (response.ok) {
			// 200...299 response
			responseData = safeGetTextAsJson(responseText) || null;
			console.debug(ComponentName, `${FunctionName}() responseData`, { responseData });
		} else {
			// 300...5xx response
			console.debug(ComponentName, `${FunctionName}() responseText [status=${responseStatus}]`, { responseText });
			if (!isEmptyString(responseText)) {
				const errorResponseData = safeGetTextAsJson(responseText);
				throw errorResponseData || new Error(`The MGUMS service returned error ${responseStatus} ${responseStatusText}: ${responseText}`);
			} else {
				throw new Error(`The MGUMS service returned error: ${responseStatus} ${responseStatusText}`);
			}
		}

		return responseData?.value || false;
	} catch (error) {
		const errMessage = digProperty(error, "message") || "";
		error.message = `${ComponentName}::${FunctionName}() ➜ Error validating organisation: ${errMessage}`;
		console.error(`Error fetching organisations: ${error?.message || "Unknown"}`, error);
		if (error instanceof Error) {
			error.name = `${ComponentName}::${FunctionName}()`;
		}
		throw error;
	}
};

/**
 * Submits a user access request to the MGUMS service.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.userEmail - The email of the user requesting access.
 * @param {string} params.displayName - The display name of the user.
 * @param {Object} params.organisation - The organisation details.
 * @param {string} params.userComments - Optional comments from the user.
 * @returns {Promise} Resolves with an object containing the success status, status type, and message.
 * ```
 * {
 *		success: true | false,
 *		statusType: "success" | "warning",
 *		message: ""
 * }
 * ```
 */
export const submitUserAccessRequest = async ({ userEmail, displayName, organisation, userComments }) => {
	const FunctionName = "submitUserAccessRequest";
	try {
		let endpoint = "/api/swaSubmitUserAccessRequest";
		const response = await fetch(endpoint, {
			method: "POST",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json"
			},
			body: JSON.stringify({
				emailAddress: (userEmail || "").trim().toLowerCase(),
				displayName: (displayName || "").trim(),
				userComments: (userComments || "").trim(),
				memberId: (organisation?.memberId || "").trim(),
			})
		});

		const responseStatus = response?.status ?? 0;
		const responseStatusText = response?.statusText || "";
		const responseText = await safeGetResponseBodyAsText(response);
		let responseData = null;
		if (response.ok) {
			// 200...299 response
			responseData = safeGetTextAsJson(responseText) || null;
			console.debug(ComponentName, `${FunctionName}() responseData`, { responseData });
		} else {
			// 300...5xx response
			console.debug(ComponentName, `${FunctionName}() responseText [status=${responseStatus}]`, { responseText });
			if (!isEmptyString(responseText)) {
				const errorResponseData = safeGetTextAsJson(responseText);
				throw errorResponseData || new Error(`The MGUMS service returned error ${responseStatus} ${responseStatusText}: ${responseText}`);
			} else {
				throw new Error(`The MGUMS service returned error: ${responseStatus} ${responseStatusText}`);
			}
		}

		return {
			success: responseData?.success || false,
			statusType: responseData?.statusType || "warning",
			message: responseData?.message || "Your request could not be submitted, please try again later"
		};
	} catch (error) {
		const errMessage = digProperty(error, "message") || "";
		error.message = `${ComponentName}::${FunctionName}() ➜ Error submitting request: ${errMessage}`;
		console.error(`Error submitting request: ${error?.message || "Unknown"}`, error);
		if (error instanceof Error) {
			error.name = `${ComponentName}::${FunctionName}()`;
		}
		throw error;
	}
};
